export const CUSTOMER_FAVOURITES_SCHEMA_VERSION = 1 as const;
export const CUSTOMER_FAVOURITES_PAGE_SIZE_MAX = 50;
export const CUSTOMER_FAVOURITES_LOCALES = ["en", "ro"] as const;
export type CustomerFavouritesLocale =
  (typeof CUSTOMER_FAVOURITES_LOCALES)[number];

export const CUSTOMER_FAVOURITE_ENTITY_TYPES = ["location"] as const;
export type CustomerFavouriteEntityType =
  (typeof CUSTOMER_FAVOURITE_ENTITY_TYPES)[number];

export type CustomerFavouriteId = string;
export type CustomerFavouriteLocationId = string;

export type CustomerFavouriteActionCapability =
  | { status: "allowed" }
  | { status: "unavailable"; reason: "provider-disabled" };

export interface CustomerFavouritesCapabilities {
  add: CustomerFavouriteActionCapability;
  remove: CustomerFavouriteActionCapability;
}

export interface CustomerFavouriteLocationReferenceV1 {
  favouriteId: CustomerFavouriteId;
  entityType: "location";
  locationId: CustomerFavouriteLocationId;
  savedAtEpochMs: number;
}

export interface CustomerFavouritesPagination {
  cursor: string | null;
  nextCursor: string | null;
  pageSize: number;
  returnedCount: number;
  totalCount: number;
}

export interface CustomerFavouritesSnapshotV1 {
  schemaVersion: typeof CUSTOMER_FAVOURITES_SCHEMA_VERSION;
  kind: "customer-favourites-snapshot";
  customerAccountId: string;
  providerId: string;
  providerRevision: string;
  snapshotId: string;
  snapshotRevision: string;
  displayRevision: string;
  generatedAtEpochMs: number;
  capabilities: CustomerFavouritesCapabilities;
  favourites: readonly CustomerFavouriteLocationReferenceV1[];
  items: readonly CustomerFavouriteLocationItem[];
  rejected: readonly CustomerFavouriteLocationRejectedReference[];
  pagination: CustomerFavouritesPagination;
}

export interface CustomerFavouritesOwner {
  customerAccountId: string;
  providerId: string;
  providerRevision: string;
  snapshotId: string;
  snapshotRevision: string;
  displayRevision: string;
}

export interface CustomerFavouriteLocationImage {
  imageId: string;
  url: string;
  alt: string;
}

export interface CustomerFavouriteLocationBadge {
  badgeId: string;
  label: string;
}

export interface CustomerFavouriteLocationCatalogEntry {
  locationId: CustomerFavouriteLocationId;
  displayRevision: string;
  businessId: string;
  businessName: string;
  locationName: string;
  publicationState: "public" | "hidden" | "withdrawn";
  businessStatus: "open" | "closed";
  bookingStatus: "available" | "unavailable" | "unknown";
  images: readonly CustomerFavouriteLocationImage[];
  rating: number | null;
  reviewCount: number | null;
  address: string | null;
  category: string | null;
  badge: CustomerFavouriteLocationBadge | null;
  destination: string | null;
}

export interface CustomerFavouriteLocationCatalog {
  schemaVersion: typeof CUSTOMER_FAVOURITES_SCHEMA_VERSION;
  kind: "customer-favourite-location-catalog";
  displayRevision: string;
  locations: readonly CustomerFavouriteLocationCatalogEntry[];
}

export type CustomerFavouriteLocationAvailability =
  | { status: "available"; reason: null }
  | { status: "unknown"; reason: "availability-unverified" }
  | {
      status: "unavailable";
      reason: "business-closed" | "booking-unavailable";
    };

export type CustomerFavouriteLocationViewCapability =
  | { status: "allowed"; destination: string }
  | { status: "unavailable"; reason: "destination-unavailable" };

export type CustomerFavouriteLocationBookingCapability =
  | { status: "allowed" }
  | { status: "unknown"; reason: "availability-unverified" }
  | {
      status: "unavailable";
      reason: "business-closed" | "booking-unavailable";
    };

export interface CustomerFavouriteLocationItem {
  favouriteId: CustomerFavouriteId;
  entityType: "location";
  locationId: CustomerFavouriteLocationId;
  savedAtEpochMs: number;
  displayRevision: string;
  businessId: string;
  businessName: string;
  locationName: string;
  publicationState: "public";
  businessStatus: "open" | "closed";
  bookingStatus: "available" | "unavailable" | "unknown";
  images: readonly CustomerFavouriteLocationImage[];
  rating: number | null;
  reviewCount: number | null;
  address: string | null;
  category: string | null;
  badge: CustomerFavouriteLocationBadge | null;
  destination: string | null;
  availability: CustomerFavouriteLocationAvailability;
  viewCapability: CustomerFavouriteLocationViewCapability;
  bookingCapability: CustomerFavouriteLocationBookingCapability;
  capabilities: CustomerFavouritesCapabilities;
}

export type CustomerFavouriteLocationRejectionReason =
  | "duplicate-favourite-id"
  | "duplicate-location-id"
  | "unknown-location"
  | "hidden-location"
  | "withdrawn-location";

export interface CustomerFavouriteLocationRejectedReference {
  reference: CustomerFavouriteLocationReferenceV1;
  reason: CustomerFavouriteLocationRejectionReason;
}

export type CustomerFavouriteLocationJoinResult =
  | {
      valid: true;
      owner: CustomerFavouritesOwner;
      items: readonly CustomerFavouriteLocationItem[];
      rejected: readonly CustomerFavouriteLocationRejectedReference[];
    }
  | {
      valid: false;
      reason:
        | "snapshot-invalid"
        | "catalog-invalid"
        | "display-revision-mismatch";
    };

export interface CustomerFavouritesReadRequest {
  customerAccountId: string;
  locale: CustomerFavouritesLocale;
  catalog: CustomerFavouriteLocationCatalog;
  displayRevision: string;
  snapshotRevision: string | null;
  cursor: string | null;
  pageSize: number;
  requestKey: string;
}

export interface CustomerFavouritesProviderResultIdentity {
  providerId: string;
  providerRevision: string;
  requestKey: string;
  customerAccountId: string;
  locale: CustomerFavouritesLocale;
  displayRevision: string;
  requestedSnapshotRevision: string | null;
  cursor: string | null;
  pageSize: number;
}

export const CUSTOMER_FAVOURITES_PARTIAL_FIELDS = [
  "favourites",
  "pagination",
  "capabilities",
  "display",
] as const;

export type CustomerFavouritesPartialField =
  (typeof CUSTOMER_FAVOURITES_PARTIAL_FIELDS)[number];

export type CustomerFavouritesProviderErrorReason =
  | "invalid-request"
  | "request-aborted"
  | "provider-request-failed"
  | "provider-response-invalid";

export type CustomerFavouritesProviderResult =
  | (CustomerFavouritesProviderResultIdentity & {
      status: "ready";
      snapshot: CustomerFavouritesSnapshotV1;
    })
  | (CustomerFavouritesProviderResultIdentity & {
      status: "empty";
      snapshot: CustomerFavouritesSnapshotV1;
    })
  | (CustomerFavouritesProviderResultIdentity & {
      status: "loading";
    })
  | (CustomerFavouritesProviderResultIdentity & {
      status: "partial";
      snapshot: CustomerFavouritesSnapshotV1;
      snapshotRevision: string;
      reason: "provider-marked-partial";
      missingFields: readonly CustomerFavouritesPartialField[];
    })
  | (CustomerFavouritesProviderResultIdentity & {
      status: "stale";
      snapshot: CustomerFavouritesSnapshotV1;
      snapshotRevision: string;
      reason:
        | "provider-marked-stale"
        | "snapshot-revision-superseded"
        | "display-revision-superseded";
    })
  | (CustomerFavouritesProviderResultIdentity & {
      status: "unavailable";
      reason: "favourites-provider-unavailable";
    })
  | (CustomerFavouritesProviderResultIdentity & {
      status: "error";
      reason: CustomerFavouritesProviderErrorReason;
      retryable: boolean;
    })
  | (CustomerFavouritesProviderResultIdentity & {
      status: "auth-expired";
      reason: "customer-session-expired";
    })
  | (CustomerFavouritesProviderResultIdentity & {
      status: "customer-mismatch";
      expectedCustomerAccountId: string;
      receivedCustomerAccountId: string;
    });

export type CustomerFavouritesPageProviderResult = Extract<
  CustomerFavouritesProviderResult,
  { status: "ready" | "empty" }
>;

export type CustomerFavouritesInspectableProviderResult = Extract<
  CustomerFavouritesProviderResult,
  { status: "ready" | "empty" | "partial" | "stale" }
>;

/** @deprecated Prefer the more explicit page-only alias. */
export type CustomerFavouritesDisplayableProviderResult =
  CustomerFavouritesPageProviderResult;

export interface CustomerFavouritesProviderResultExpectation {
  providerId: string;
  providerRevision: string;
  requestKey: string;
  customerAccountId: string;
  locale: CustomerFavouritesLocale;
  displayRevision: string;
  snapshotRevision: string | null;
  cursor: string | null;
  pageSize: number;
  catalog: CustomerFavouriteLocationCatalog;
}

export interface CustomerFavouritesProvider {
  readonly providerId: string;
  readonly providerRevision: string;
  resolve(
    request: CustomerFavouritesReadRequest,
    signal?: AbortSignal,
  ): Promise<CustomerFavouritesProviderResult>;
}
